import { v4 as uuidv4 } from 'uuid';
import { ICartService } from '@/domain/services/cart.service.interface';
import {
  CartEntity,
  CartItemEntity as StoredCartItem,
  CartTotals,
} from '@/domain/entities/cart.entity';
import { CartItemEntity } from '@/domain/entities/cart-item.entity';
import { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';
import {
  StorefrontBffError,
  type Cart,
  type CartItem,
  type CartItemInput,
} from '@template-project/storefront-bff-client';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import {
  applyPackAnnotations,
  forgetPack,
  rememberPackLines,
} from '@/lib/cart/pack-cart-annotations';
import { env } from '@/lib/utils/env';
import { Logger } from '@template-project/storefront-shared';

function defaultCartStore(shop?: string): string {
  return shop ?? (env.FUNNELS_STORE_ID || 'demo');
}

function dollarStringToCents(dollars: string): CurrencyAmount {
  const cents = Math.round(Number.parseFloat(dollars) * 100);
  return { base: cents, display: cents };
}

function multiCurrencyPriceToCents(price: { store: string; presentment: string }): CurrencyAmount {
  return {
    base: Math.round(Number.parseFloat(price.store) * 100),
    display: Math.round(Number.parseFloat(price.presentment) * 100),
  };
}

const FUNNEL_OFFER_PROOF_STORAGE_PREFIX = 'tss:funnel-offer-proof';

function funnelOfferProofStorageKey(
  cartToken: string,
  redemptionId: string,
): string {
  return `${FUNNEL_OFFER_PROOF_STORAGE_PREFIX}:${cartToken}:${redemptionId}`;
}

function rememberFunnelOfferProof(
  cartToken: string,
  cart: Cart,
  productId: string,
  variantId: string,
  previousRedemptionIds: ReadonlySet<string>,
  proof: string,
): void {
  const acceptedLine = [...cart.items]
    .reverse()
    .find(
      (item) =>
        item.product_id === productId &&
        item.variant_id === variantId &&
        typeof item.funnel_offer_id === 'string' &&
        item.funnel_offer_id.trim().length > 0 &&
        typeof item.redemption_id === 'string' &&
        !previousRedemptionIds.has(item.redemption_id),
    );
  if (!acceptedLine?.redemption_id || typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      funnelOfferProofStorageKey(cartToken, acceptedLine.redemption_id),
      proof,
    );
  } catch {
    // The cart mutation already succeeded. A storage policy failure must not
    // report a false commerce failure; later cart rewrites fail closed instead.
  }
}

function readFunnelOfferProof(
  cartToken: string,
  redemptionId: string,
): string | undefined {
  if (typeof sessionStorage === 'undefined') {
    return undefined;
  }
  try {
    return (
      sessionStorage.getItem(
        funnelOfferProofStorageKey(cartToken, redemptionId),
      ) ?? undefined
    );
  } catch {
    return undefined;
  }
}

function mapBffCartItemToInput(
  item: StoredCartItem,
  cartToken: string,
): CartItemInput {
  const requiresFunnelOfferProof =
    typeof item.funnel_offer_id === 'string' &&
    item.funnel_offer_id.trim().length > 0;
  const funnelOfferToken = requiresFunnelOfferProof && item.redemption_id
    ? readFunnelOfferProof(cartToken, item.redemption_id)
    : undefined;
  if (requiresFunnelOfferProof && !funnelOfferToken) {
    throw new Error(
      'Cart rewrite requires the original funnel offer proof for every retained offer line',
    );
  }
  return {
    product_uuid: item.product_id,
    variant_uuid: item.variant_id,
    quantity: item.quantity,
    ...(item.pack_id ? { pack_id: item.pack_id } : {}),
    ...(item.pack_item_id ? { pack_item_id: item.pack_item_id } : {}),
    ...(funnelOfferToken ? { funnel_offer_token: funnelOfferToken } : {}),
    ...(item.subscription ? { subscription: item.subscription } : {}),
  };
}

function mapBffCartToEntity(cart: Cart): CartEntity {
  const items: StoredCartItem[] = cart.items.map((item: CartItem, index: number) => ({
    id:
      item.line_id ??
      (item.pack_id
        ? `${item.pack_id}-${item.pack_item_id ?? item.variant_id}-${index}`
        : `${item.product_id}-${item.variant_id}-${index}`),
    line_id: item.line_id,
    product_id: item.product_id,
    variant_id: item.variant_id,
    quantity: item.quantity,
    name: item.name,
    price: multiCurrencyPriceToCents(item.unit_price),
    image_url: item.image_url,
    compare_at_price: item.compare_at_unit_price
      ? multiCurrencyPriceToCents(item.compare_at_unit_price)
      : item.compare_at_price,
    pack_id: item.pack_id,
    pack_item_id: item.pack_item_id,
    variation_attributes: item.variation_attributes,
    discount_type: item.discount_type,
    campaign_id: item.campaign_id,
    redemption_id: item.redemption_id,
    funnel_id: item.funnel_id,
    funnel_step_id: item.funnel_step_id,
    funnel_offer_id: item.funnel_offer_id,
    ...(item.subscription ? { subscription: item.subscription } : {}),
  }));

  const mapTotals = (t: Cart['totals']): CartTotals => ({
    items: dollarStringToCents(t.items),
    discounts: dollarStringToCents(t.discounts),
    taxes: dollarStringToCents(t.taxes),
    shipping: dollarStringToCents(t.shipping),
    fees: dollarStringToCents(t.fees),
    total: dollarStringToCents(t.total),
  });

  return {
    token: cart.token,
    items,
    totals: mapTotals(cart.totals),
    currency: cart.currency.main,
    country_code: cart.country_code,
    vendor: cart.vendor,
    shop: cart.store,
    status: cart.status,
    upsell_funnel_id: cart.upsell_funnel_id,
    upsell_first_step_id: cart.upsell_first_step_id,
    pricing: cart.pricing,
  };
}

function cartCurrencyRatio(options?: { currencyRatio?: number }): number {
  const ratio = options?.currencyRatio;
  if (ratio === undefined || !Number.isFinite(ratio) || ratio <= 0) {
    return 1;
  }
  return ratio;
}

export class CartBffService implements ICartService {
  private readonly client = createBffClient();

  async getCart(token: string): Promise<CartEntity | null> {
    try {
      const res = await this.client.getCart(token);
      return applyPackAnnotations(mapBffCartToEntity(res.data), token);
    } catch (err: unknown) {
      if (err instanceof StorefrontBffError && err.status === 404) return null;
      const message = err instanceof Error ? err.message : String(err);
      Logger.error(`[CartBffService] getCart error: ${message}`);
      throw err;
    }
  }

  async createCart(options?: {
    countryCode?: string;
    currency?: string;
    currencyRatio?: number;
    vendor?: string;
    shop?: string;
    upsellFunnelId?: string;
    upsellFirstStepId?: string;
    cartToken?: string;
  }): Promise<CartEntity> {
    const cartToken = options?.cartToken ?? uuidv4();
    try {
      const res = await this.client.createCart({
        token: cartToken,
        vendor: options?.vendor ?? 'shapermint',
        store: defaultCartStore(options?.shop),
        country_code: options?.countryCode ?? 'US',
        currency_code: options?.currency ?? 'USD',
        local_currency_code: options?.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: [],
        ...(options?.upsellFunnelId ? { upsell_funnel_id: options.upsellFunnelId } : {}),
        ...(options?.upsellFirstStepId ? { upsell_first_step_id: options.upsellFirstStepId } : {}),
      });
      return mapBffCartToEntity(res.data);
    } catch (err) {
      Logger.error(`[CartBffService] createCart error: ${String(err)}`);
      throw err;
    }
  }

  async createCartWithItem(
    item: CartItemEntity,
    options?: {
      countryCode?: string;
      currency?: string;
      currencyRatio?: number;
      vendor?: string;
      shop?: string;
      upsellFunnelId?: string;
      upsellFirstStepId?: string;
      cartToken?: string;
      funnelOfferToken?: string;
    }
  ): Promise<{ success: boolean; cartToken: string; cart?: CartEntity }> {
    const cartToken = options?.cartToken ?? uuidv4();
    try {
      const response = await this.client.createCart({
        token: cartToken,
        vendor: options?.vendor ?? 'shapermint',
        store: defaultCartStore(options?.shop),
        country_code: options?.countryCode ?? 'US',
        currency_code: options?.currency ?? 'USD',
        local_currency_code: options?.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: [
          {
            product_uuid: item.product_id,
            variant_uuid: item.variant_id,
            quantity: item.quantity,
            ...(options?.funnelOfferToken ? { funnel_offer_token: options.funnelOfferToken } : {}),
            ...(item.subscription ? { subscription: item.subscription } : {}),
          },
        ],
        ...(options?.upsellFunnelId ? { upsell_funnel_id: options.upsellFunnelId } : {}),
        ...(options?.upsellFirstStepId ? { upsell_first_step_id: options.upsellFirstStepId } : {}),
      });
      if (options?.funnelOfferToken) {
        rememberFunnelOfferProof(
          cartToken,
          response.data,
          item.product_id,
          item.variant_id,
          new Set(),
          options.funnelOfferToken,
        );
      }
      return { success: true, cartToken, cart: mapBffCartToEntity(response.data) };
    } catch (err) {
      Logger.error(`[CartBffService] createCartWithItem error: ${String(err)}`);
      throw err;
    }
  }

  async addToCart(
    cartToken: string,
    item: CartItemEntity,
    options?: {
      currencyRatio?: number;
      upsellFunnelId?: string;
      upsellFirstStepId?: string;
      funnelOfferToken?: string;
    }
  ): Promise<{ success: boolean; cartToken: string; cart?: CartEntity }> {
    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const existingItems = currentCart.items.map((item) =>
      mapBffCartItemToInput(item, cartToken),
    );
    const previousRedemptionIds = new Set(
      currentCart.items.flatMap((item) =>
        item.redemption_id ? [item.redemption_id] : [],
      ),
    );

    const existingIndex = existingItems.findIndex(
      (i) => i.product_uuid === item.product_id && i.variant_uuid === item.variant_id && !i.pack_id
    );

    if (existingIndex === -1 || options?.funnelOfferToken) {
      existingItems.push({
        product_uuid: item.product_id,
        variant_uuid: item.variant_id,
        quantity: item.quantity,
        ...(options?.funnelOfferToken ? { funnel_offer_token: options.funnelOfferToken } : {}),
        ...(item.subscription ? { subscription: item.subscription } : {}),
      });
    } else {
      existingItems[existingIndex].quantity += item.quantity;
    }

    try {
      const response = await this.client.updateCart(cartToken, {
        token: cartToken,
        vendor: currentCart.vendor ?? 'shapermint',
        store: defaultCartStore(currentCart.shop),
        country_code: currentCart.country_code ?? 'US',
        currency_code: currentCart.currency ?? 'USD',
        local_currency_code: currentCart.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: existingItems,
        ...(options?.upsellFunnelId || currentCart.upsell_funnel_id
          ? { upsell_funnel_id: options?.upsellFunnelId ?? currentCart.upsell_funnel_id }
          : {}),
        ...(options?.upsellFirstStepId || currentCart.upsell_first_step_id
          ? { upsell_first_step_id: options?.upsellFirstStepId ?? currentCart.upsell_first_step_id }
          : {}),
      });
      if (options?.funnelOfferToken) {
        rememberFunnelOfferProof(
          cartToken,
          response.data,
          item.product_id,
          item.variant_id,
          previousRedemptionIds,
          options.funnelOfferToken,
        );
      }
      return { success: true, cartToken, cart: mapBffCartToEntity(response.data) };
    } catch (err) {
      Logger.error(`[CartBffService] addToCart error: ${String(err)}`);
      throw err;
    }
  }

  async removeCartItem(
    cartToken: string,
    productId: string,
    variantId: string,
    lineId?: string,
    idempotencyKey?: string,
  ): Promise<CartEntity> {
    if (!productId || !variantId) {
      throw new Error('Product and variant are required');
    }
    const response = await this.client.removeCartItem(
      cartToken,
      {
        token: cartToken,
        line: lineId
          ? { line_id: lineId, line_type: 'standard' }
          : { variant_id: variantId, line_type: 'standard' },
      },
      idempotencyKey?.trim() || uuidv4(),
    );
    return applyPackAnnotations(mapBffCartToEntity(response.data), cartToken);
  }

  async updateCartItem(
    cartToken: string,
    productId: string,
    variantId: string,
    quantity: number,
    lineId?: string,
  ): Promise<CartEntity> {
    if (quantity <= 0) {
      return this.removeCartItem(cartToken, productId, variantId, lineId);
    }

    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items.map((i) => ({
      ...mapBffCartItemToInput(i, cartToken),
      quantity:
        (lineId
          ? i.line_id === lineId
          : i.product_id === productId && i.variant_id === variantId)
          ? quantity
          : i.quantity,
    }));

    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async removePackFromCart(cartToken: string, packId: string): Promise<CartEntity> {
    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items
      .filter((i) => i.pack_id !== packId)
      .map((item) => mapBffCartItemToInput(item, cartToken));

    forgetPack(cartToken, packId);
    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async updatePackQuantity(
    cartToken: string,
    packId: string,
    quantity: number
  ): Promise<CartEntity> {
    if (quantity <= 0) {
      return this.removePackFromCart(cartToken, packId);
    }

    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items.map((i) => ({
      ...mapBffCartItemToInput(i, cartToken),
      quantity: i.pack_id === packId ? quantity : i.quantity,
    }));

    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async addPackToCart(
    cartToken: string,
    packId: string,
    selections?: Array<{ packItemId?: string; productId: string; variantId: string }>
  ): Promise<{ success: boolean; cartToken: string }> {
    try {
      await this.client.addPackToCart(cartToken, {
        pack_id: packId,
        selections: selections?.map((s) => ({
          pack_item_id: s.packItemId,
          product_id: s.productId,
          variant_id: s.variantId,
        })),
      });
      if (selections?.length) {
        rememberPackLines(cartToken, packId, selections);
      }
      return { success: true, cartToken };
    } catch (error) {
      Logger.error(`[CartBffService] addPackToCart error: ${error}`);
      throw error;
    }
  }

  private async putCart(
    cartToken: string,
    currentCart: CartEntity,
    items: Array<{
      product_uuid: string;
      variant_uuid: string;
      quantity: number;
      pack_id?: string;
      pack_item_id?: string;
      subscription?: CartItemInput['subscription'];
    }>
  ): Promise<CartEntity> {
    try {
      const res = await this.client.updateCart(cartToken, {
        token: cartToken,
        vendor: currentCart.vendor ?? 'shapermint',
        store: defaultCartStore(currentCart.shop),
        country_code: currentCart.country_code ?? 'US',
        currency_code: currentCart.currency ?? 'USD',
        local_currency_code: currentCart.currency ?? 'USD',
        currency_ratio: 1,
        items,
      });
      return applyPackAnnotations(mapBffCartToEntity(res.data), cartToken);
    } catch (err) {
      Logger.error(`[CartBffService] putCart error: ${String(err)}`);
      throw err;
    }
  }
}
